import { cn } from "@/lib/utils";
import { SmartImage } from "@/components/SmartImage";
import type { Photo } from "@/lib/photos";

type FramedImageProps = {
  photo: Photo;
  sizes: string;
  index?: string;
  label?: string;
  ratio?: string;
  onOpen?: () => void;
  className?: string;
  imgClassName?: string;
};

export function FramedImage({
  photo,
  sizes,
  index,
  label,
  ratio = "aspect-[2/3]",
  onOpen,
  className,
  imgClassName,
}: FramedImageProps) {
  const frame = (
    <div className="border border-border/70 bg-background p-2.5 transition-colors duration-700 group-hover:border-gold/70 sm:p-4">
      <div className="relative overflow-hidden bg-muted">
        <SmartImage
          photo={photo}
          sizes={sizes}
          className={cn("w-full", ratio)}
          imgClassName={cn(
            "transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.035]",
            imgClassName,
          )}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 border border-foreground/10"
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-espresso/0 transition-colors duration-700 group-hover:bg-espresso/10"
        />
      </div>
    </div>
  );

  return (
    <figure className={cn("group", className)}>
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Open larger: ${photo.alt}`}
          className="block w-full cursor-zoom-in"
        >
          {frame}
        </button>
      ) : (
        frame
      )}

      {(index || label) && (
        <figcaption className="mt-4 flex items-baseline gap-3">
          {index && <span className="font-display text-sm text-gold">{index}</span>}
          {label && (
            <span className="font-sans text-[0.625rem] tracking-[0.24em] text-muted-foreground uppercase">
              {label}
            </span>
          )}
        </figcaption>
      )}
    </figure>
  );
}
